import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { EnvelopeIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';

export default function ProposalSection({ gym, canEdit, onUpdate }) {
  const isProposalStage = ['Proposal Sent', 'Agreement Signed', 'Facility Work',
    'Post Reki Approved', 'Go-Live Ready', 'Go-Live Done'].includes(gym.stage);

  if (!isProposalStage) return null;

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    proposedAmount: gym.proposedAmount || '',
    partnershipReceivingAmount: gym.partnershipReceivingAmount || '',
  });
  const [edited, setEdited] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/gyms/${gym.id}`, {
        proposedAmount: form.proposedAmount ? parseFloat(form.proposedAmount) : null,
        partnershipReceivingAmount: form.partnershipReceivingAmount ? parseFloat(form.partnershipReceivingAmount) : null,
      });
      toast.success('Proposal amounts saved');
      setEdited(false);
      onUpdate?.();
    } catch {
      toast.error('Failed to save proposal amounts');
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async (type) => {
    const labels = {
      proposal: 'Proposal email',
      acknowledgement: 'Acknowledgement email',
      acceptance: 'Confirmation of acceptance email',
    };
    try {
      await axios.post(`/api/gyms/${gym.id}/send-proposal-email`, { type });
      toast.success(`${labels[type]} sent successfully`);
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to send ${labels[type]}`);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mt-4">
      <h3 className="text-base font-semibold text-gray-800 mb-4">Proposal Details</h3>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Proposed Amount (₹)
          </label>
          {canEdit ? (
            <input
              type="number" min="0"
              value={form.proposedAmount}
              onChange={e => { setForm(p => ({ ...p, proposedAmount: e.target.value })); setEdited(true); }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500"
              placeholder="e.g. 5000000"
            />
          ) : (
            <p className="text-sm font-semibold text-gray-900">
              {gym.proposedAmount ? `₹${Number(gym.proposedAmount).toLocaleString('en-IN')}` : '—'}
            </p>
          )}
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
            Partnership Receiving Amount (₹)
          </label>
          {canEdit ? (
            <input
              type="number" min="0"
              value={form.partnershipReceivingAmount}
              onChange={e => { setForm(p => ({ ...p, partnershipReceivingAmount: e.target.value })); setEdited(true); }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500"
              placeholder="e.g. 2500000"
            />
          ) : (
            <p className="text-sm font-semibold text-gray-900">
              {gym.partnershipReceivingAmount ? `₹${Number(gym.partnershipReceivingAmount).toLocaleString('en-IN')}` : '—'}
            </p>
          )}
        </div>
      </div>

      {edited && canEdit && (
        <button onClick={handleSave} disabled={saving}
          className="text-sm bg-red-600 text-white px-4 py-1.5 rounded-lg hover:bg-red-700 mb-4 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Amounts'}
        </button>
      )}

      <div className="border-t border-gray-100 pt-4 mt-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Email Flows</p>
        <div className="flex flex-wrap gap-3">
          <EmailAction
            label="Send Proposal Mail"
            sent={!!gym.proposalSentAt}
            sentAt={gym.proposalSentAt}
            onClick={() => sendEmail('proposal')}
            disabled={!canEdit}
          />
          <EmailAction
            label="Acknowledgement Mail"
            sent={!!gym.proposalAcknowledgedAt}
            sentAt={gym.proposalAcknowledgedAt}
            onClick={() => sendEmail('acknowledgement')}
            disabled={!canEdit || !gym.proposalSentAt}
            tooltip={!gym.proposalSentAt ? 'Send proposal mail first' : ''}
          />
          <EmailAction
            label="Confirmation of Acceptance"
            sent={!!gym.proposalAcceptedAt}
            sentAt={gym.proposalAcceptedAt}
            onClick={() => sendEmail('acceptance')}
            disabled={!canEdit || !gym.proposalAcknowledgedAt}
            tooltip={!gym.proposalAcknowledgedAt ? 'Send acknowledgement first' : ''}
          />
        </div>
      </div>
    </div>
  );
}

function EmailAction({ label, sent, sentAt, onClick, disabled, tooltip }) {
  return (
    <div className="relative group">
      <button
        onClick={onClick}
        disabled={disabled || sent}
        className={`flex items-center gap-2 text-sm px-4 py-2 rounded-lg border font-medium transition-colors
          ${sent ? 'bg-green-50 border-green-200 text-green-700 cursor-default' :
            disabled ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed' :
            'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
      >
        {sent ? <CheckBadgeIcon className="h-4 w-4" /> : <EnvelopeIcon className="h-4 w-4" />}
        {label}
        {sent && sentAt && (
          <span className="text-xs font-normal text-green-600">
            · {new Date(sentAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
          </span>
        )}
      </button>
      {tooltip && disabled && !sent && (
        <div className="absolute bottom-full left-0 mb-1 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
          {tooltip}
        </div>
      )}
    </div>
  );
}
